import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { verisureService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { VerisureBlankQr, ItemCategory, VerisureItem } from '../../types';
import { toast } from 'react-hot-toast';
import { VerisureQrCardModal } from '../../components/verisure/VerisureQrCardModal';
import { VerisureQrCode } from '../../components/verisure/VerisureQrCode';
import { subscriptionService, SubscriptionUsage } from '../../services/subscriptionService';
import { UpgradeModal } from '../../components/subscription/UpgradeModal';

const POPULAR_BRANDS: Record<string, string[]> = {
  consumer_electronics: ['Apple', 'Samsung', 'Sony', 'Dell', 'HP', 'Lenovo', 'LG', 'Google', 'Asus', 'Microsoft'],
  home_appliance: ['Samsung', 'LG', 'Whirlpool', 'Panasonic', 'Bosch', 'Hisense', 'Thermocool', 'Haier'],
  industrial: ['Caterpillar', 'Cummins', 'Perkins', 'Mikano', 'Schneider Electric', 'Siemens', 'Komatsu', 'ABB'],
  electrical: ['Schneider Electric', 'ABB', 'Siemens', 'Eaton', 'Legrand', 'Havells'],
  vehicle: ['Toyota', 'Honda', 'Mercedes-Benz', 'Ford', 'Hyundai', 'Kia', 'BMW', 'Nissan'],
  telecom: ['Fujikura', 'Cisco', 'Huawei', 'MikroTik', 'Ubiquiti', 'ZTE'],
  other: ['Other / Generic'],
};

export const TagDevicePage: React.FC = () => {
  const { tagId: urlTagId } = useParams<{ tagId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const isSelfOnboarding = !urlTagId;
  const [tagId, setTagId] = useState(
    urlTagId || `VRS-TAG-${Math.floor(10000 + Math.random() * 90000)}`
  );
  const [blankQr, setBlankQr] = useState<VerisureBlankQr | null>(null);
  const [existingItem, setExistingItem] = useState<VerisureItem | null>(null);
  const [loading, setLoading] = useState(Boolean(urlTagId));
  const [submitting, setSubmitting] = useState(false);
  const [isActivated, setIsActivated] = useState(false);
  const [activatedItem, setActivatedItem] = useState<any>(null);
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  const [subscriptionUsage, setSubscriptionUsage] = useState<SubscriptionUsage | null>(null);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  // Form Fields
  const [category, setCategory] = useState<ItemCategory>('consumer_electronics');
  const [make, setMake] = useState('Apple');
  const [customMake, setCustomMake] = useState('');
  const [model, setModel] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [conditionScore, setConditionScore] = useState<number>(100);
  const [estimatedPrice, setEstimatedPrice] = useState<number | ''>('');
  const [priceCurrency, setPriceCurrency] = useState('NGN');

  // Owner Info
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerPhone, setOwnerPhone] = useState('');
  const [attachedConfirmed, setAttachedConfirmed] = useState(true);

  const loadUsage = async () => {
    try {
      const u = await subscriptionService.getUsageAndLimits(user);
      setSubscriptionUsage(u);
    } catch (e) {
      console.warn('Error loading subscription usage:', e);
    }
  };

  useEffect(() => {
    loadUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (user?.name && !ownerName) setOwnerName(user.name);
    if (user?.email && !ownerEmail) setOwnerEmail(user.email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const checkTag = async () => {
      if (!urlTagId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // 1. Check if device is already registered by this tag ID
        const itemRes = await verisureService.getItemById(tagId);
        if (itemRes.data && itemRes.data.make) {
          setExistingItem(itemRes.data);
          setLoading(false);
          return;
        }

        // 2. Check if blank QR exists in system
        const blankRes = await verisureService.getBlankQrByTagId(tagId);
        if (blankRes.data) {
          setBlankQr(blankRes.data);
          if (blankRes.data.status === 'registered' && blankRes.data.deviceSummary) {
            // Already claimed
            setExistingItem({
              assetId: tagId,
              make: blankRes.data.deviceSummary.make,
              model: blankRes.data.deviceSummary.model,
              category: blankRes.data.deviceSummary.category as ItemCategory,
              serialNumber: blankRes.data.deviceSummary.serialNumber,
              ownerName: blankRes.data.claimedByName || 'Registered Owner',
              conditionScore: 100,
              qrCodeUrl: `${window.location.origin}/item/${tagId}`,
            });
          }
        } else {
          // If tagId format looks like a Verisure tag (e.g. VRS-TAG-...) create ad-hoc blank record
          setBlankQr({
            tagId,
            outletName: 'Official Verisure Partner Outlet',
            status: 'unclaimed',
            qrPayloadUrl: `${window.location.origin}/tag/${tagId}`,
            createdBy: 'system',
            createdAt: new Date(),
          });
        }
      } catch (err) {
        console.error('Error fetching tag data:', err);
      } finally {
        setLoading(false);
      }
    };

    checkTag();
  }, [tagId, urlTagId]);

  const handleBrandSelect = (brand: string) => {
    if (brand === 'Other / Generic') {
      setMake('Other');
    } else {
      setMake(brand);
      setCustomMake('');
    }
  };

  const handleRegisterDevice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tagId) {
      toast.error('Missing Tag ID');
      return;
    }
    if (!attachedConfirmed) {
      toast.error('Please confirm you have attached the physical QR sticker to your device.');
      return;
    }

    const finalMake = make === 'Other' ? customMake : make;
    if (!finalMake) {
      toast.error('Please specify the device brand/make');
      return;
    }
    if (!model) {
      toast.error('Please enter the device model');
      return;
    }
    if (!ownerName) {
      toast.error('Please enter the owner name');
      return;
    }

    // Enforce Freemium Limits (5 for Individuals, 10 for Organizations)
    const check = await subscriptionService.canRegisterQr(user);
    if (!check.allowed) {
      setSubscriptionUsage(check.usage);
      setIsUpgradeModalOpen(true);
      toast.error(check.message || 'Freemium QR limit reached. Please upgrade to Premium.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        make: finalMake,
        model: model.trim(),
        category,
        serialNumber: serialNumber.trim(),
        year: Number(year),
        conditionScore: Number(conditionScore),
        estimatedOriginalPrice: Number(estimatedPrice) || 0,
        priceCurrency,
        ownerName: ownerName.trim(),
        ownerEmail: ownerEmail.trim(),
        ownerPhone: ownerPhone.trim(),
        outletOrigin: isSelfOnboarding
          ? 'Digital Self-Registration'
          : blankQr?.outletName || 'Official Verisure Partner Outlet',
      };

      const res = await verisureService.claimAndRegisterDevice(tagId, payload);
      setActivatedItem(res.data.item || { ...payload, assetId: tagId });
      setIsActivated(true);
      toast.success(
        isSelfOnboarding
          ? 'Device registered & QR Identity Card generated!'
          : 'Device registered & QR tag activated successfully!'
      );
    } catch (err: any) {
      toast.error('Registration failed: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6 text-orange-400 font-bold">
        <svg className="animate-spin h-8 w-8 text-orange-500 mb-3" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
        <span>Reading Verisure Outlet QR Tag...</span>
      </div>
    );
  }

  // State A: Tag is already bound and registered to a device
  if (existingItem) {
    return (
      <div className="min-h-screen bg-slate-950 font-sans p-4 md:p-8 flex items-center justify-center">
        <div className="max-w-xl w-full bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-8 space-y-6 shadow-2xl text-center">
          <div className="w-16 h-16 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-full flex items-center justify-center text-3xl mx-auto">
            ✓
          </div>

          <div>
            <div className="text-xs font-bold text-emerald-400 uppercase tracking-widest mb-1">
              Active Registered Device
            </div>
            <h1 className="text-2xl md:text-3xl font-black text-white">
              {existingItem.make} {existingItem.model}
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              Tag ID: <span className="font-mono text-white font-bold">{existingItem.assetId}</span> • Owner: {existingItem.ownerName}
            </p>
          </div>

          <div className="bg-slate-950/70 border border-slate-800 rounded-2xl p-4 text-left space-y-2 text-xs text-slate-300">
            <div className="flex justify-between">
              <span className="text-slate-500">Serial Number / IMEI:</span>
              <span className="font-mono font-bold text-white">{existingItem.serialNumber || 'Recorded in Registry'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Device Category:</span>
              <span className="capitalize">{existingItem.category?.replace('_', ' ') || 'Consumer Electronics'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Condition Score:</span>
              <span className="text-emerald-400 font-bold">{existingItem.conditionScore || 100}/100</span>
            </div>
            {existingItem.outletOrigin && (
              <div className="flex justify-between">
                <span className="text-slate-500">Registered Outlet:</span>
                <span>{existingItem.outletOrigin}</span>
              </div>
            )}
          </div>

          <div className="space-y-3 pt-2">
            <button
              onClick={() => navigate(`/item/${existingItem.assetId || tagId}`)}
              className="w-full bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 text-white font-extrabold py-3.5 rounded-2xl shadow-lg shadow-orange-500/20 hover:opacity-95 transition flex items-center justify-center gap-2 text-sm"
            >
              View Immutable Repair Ledger & Device Passport
            </button>
            <button
              onClick={() => navigate(`/repair/log/${existingItem.assetId || tagId}`)}
              className="w-full bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 font-bold py-3.5 rounded-2xl transition flex items-center justify-center gap-2 text-sm"
            >
              Technician: Log New Repair on this Device
            </button>
          </div>
        </div>
      </div>
    );
  }

  // State B: Device just successfully registered (Celebration / Passport Card)
  if (isActivated && activatedItem) {
    return (
      <div className="min-h-screen bg-slate-950 font-sans p-4 md:p-8 flex items-center justify-center">
        <div className="max-w-xl w-full glass-card rounded-3xl p-6 md:p-8 space-y-6 shadow-2xl text-center relative overflow-hidden border border-white/[0.1]">
          {/* Top Banner Accent */}
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-red-600 via-orange-500 to-amber-500"></div>

          <div className="w-16 h-16 bg-gradient-to-br from-amber-400 via-orange-500 to-red-500 text-slate-950 rounded-2xl flex items-center justify-center mx-auto shadow-lg shadow-orange-500/25">
            <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>

          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-xs font-bold rounded-full mb-2">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
              <span>Cryptographic Passport Issued</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">
              {activatedItem.make} {activatedItem.model}
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              Permanent Identity ID: <span className="font-mono text-orange-400 font-bold tracking-wider">{activatedItem.assetId}</span>
            </p>
          </div>

          {/* Quick Embedded Verisure QR Badge */}
          <div className="bg-slate-900/60 border border-white/[0.08] rounded-2xl p-4 flex flex-col items-center justify-center">
            <div className="text-xs font-black tracking-tight text-white mb-2">
              I am <span className="text-[#ff5a00] font-black">Verisure</span>
            </div>
            <div className="p-2.5 bg-white rounded-xl shadow-inner">
              <VerisureQrCode
                value={`${window.location.origin}/item/${activatedItem.assetId}`}
                size={120}
                level="H"
                showCenterBadge={true}
                badgeTheme="light"
              />
            </div>
            <div className="text-[10px] font-mono text-slate-400 font-bold mt-2">
              TAG ID: {activatedItem.assetId}
            </div>
          </div>

          {/* Certificate Snapshot */}
          <div className="bg-[#090d16] border border-white/[0.08] rounded-2xl p-5 text-left space-y-3 text-xs text-slate-300">
            <div className="flex justify-between border-b border-white/[0.06] pb-2.5">
              <span className="text-slate-500 font-medium">Registered Owner:</span>
              <span className="font-bold text-white">{activatedItem.ownerName}</span>
            </div>
            <div className="flex justify-between border-b border-white/[0.06] pb-2.5">
              <span className="text-slate-500 font-medium">Serial / IMEI:</span>
              <span className="font-mono font-bold text-emerald-400">{activatedItem.serialNumber || 'Tamper-Secured'}</span>
            </div>
            <div className="flex justify-between border-b border-white/[0.06] pb-2.5">
              <span className="text-slate-500 font-medium">Outlet Origin:</span>
              <span className="text-slate-200 font-semibold">{activatedItem.outletOrigin}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500 font-medium">Initial Health Score:</span>
              <span className="text-emerald-400 font-extrabold">100 / 100 (Pristine / Mint)</span>
            </div>
          </div>

          <div className="p-4 bg-orange-500/10 border border-orange-500/20 rounded-2xl text-left text-xs text-orange-300 space-y-1">
            <div className="font-bold flex items-center gap-1.5 text-orange-400">
              <span>Tamper-Proof Chain of Custody Activated:</span>
            </div>
            <p className="text-[11px] text-slate-400 leading-relaxed">
              Whenever you take this hardware for servicing, certified Verisure technicians will scan your tag to log authentic replacement parts, before/after photos, and immutable proof-of-service.
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="space-y-3 pt-2">
            <button
              onClick={() => navigate(`/item/${activatedItem.assetId}`)}
              className="w-full bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 hover:opacity-95 text-white font-black py-4 rounded-2xl shadow-xl shadow-orange-500/25 transition-all flex items-center justify-center gap-2 text-sm"
            >
              Open Public Device Identity & Passport &rarr;
            </button>
            <button
              type="button"
              onClick={() => setIsQrModalOpen(true)}
              className="w-full bg-slate-900 border border-orange-500/40 hover:bg-orange-500/10 text-orange-400 font-bold py-3.5 rounded-2xl transition flex items-center justify-center gap-2 text-sm"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <rect x="7" y="7" width="3" height="3"/>
                <rect x="14" y="7" width="3" height="3"/>
                <rect x="7" y="14" width="3" height="3"/>
                <rect x="14" y="14" width="3" height="3"/>
              </svg>
              <span>Print / Download My Device QR Badge</span>
            </button>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => navigate('/technicians')}
                className="bg-slate-900 border border-white/10 hover:bg-slate-800 text-slate-200 font-bold py-3 rounded-xl transition text-xs flex items-center justify-center gap-1.5"
              >
                Find Technicians
              </button>
              <button
                onClick={() => navigate(`/repair/log/${activatedItem.assetId}`)}
                className="bg-slate-900 border border-white/10 hover:bg-slate-800 text-slate-200 font-bold py-3 rounded-xl transition text-xs flex items-center justify-center gap-1.5"
              >
                Log First Repair
              </button>
            </div>
          </div>
        </div>

        {/* QR Card Modal for Direct Printing / Saving */}
        {isQrModalOpen && activatedItem && (
          <VerisureQrCardModal
            isOpen={isQrModalOpen}
            onClose={() => setIsQrModalOpen(false)}
            value={`${window.location.origin}/item/${activatedItem.assetId}`}
            assetId={activatedItem.assetId}
            title={`${activatedItem.make} ${activatedItem.model}`}
            subtitle={`Owner: ${activatedItem.ownerName}`}
            size={activatedItem.category === 'industrial' || activatedItem.category === 'vehicle' ? 'large' : 'standard'}
            itemRoute={`/item/${activatedItem.assetId}`}
          />
        )}
      </div>
    );
  }

  // State C: Blank / Unclaimed Tag - Ready to register
  return (
    <div className="min-h-screen bg-slate-950 font-sans p-3 sm:p-6 md:p-8 min-w-0 max-w-full overflow-hidden">
      <div className="max-w-2xl mx-auto space-y-6 sm:space-y-8 min-w-0 max-w-full">
        {/* Header */}
        <div className="glass-card rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 relative overflow-hidden border border-white/[0.08]">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-white rounded-xl shadow">
              <img src="/official.png" alt="Verisure" className="h-7 w-auto object-contain" />
            </div>
            <div>
              <div className="text-[10px] font-black uppercase text-orange-400 tracking-widest">
                {isSelfOnboarding ? 'DIRECT DIGITAL REGISTRATION' : 'VERISURE OUTLET ONBOARDING'}
              </div>
              <h1 className="text-xl md:text-2xl font-black text-white">
                {isSelfOnboarding ? 'Register Appliance & Generate QR' : 'Tag Your New Device'}
              </h1>
            </div>
          </div>

          {/* Freemium Usage Meter Pill */}
          {subscriptionUsage && (
            <div className="mb-4 p-3 bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-500/30 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                <span className="text-slate-300">
                  {subscriptionUsage.isOrganization ? 'Organization Plan' : 'Individual Plan'}:
                </span>
                <span className="font-bold text-orange-400 font-mono">
                  {subscriptionUsage.tier === 'premium'
                    ? 'Unlimited Active (Premium)'
                    : `${subscriptionUsage.currentCount} / ${subscriptionUsage.limit} Free QRs Used`}
                </span>
              </div>
              {subscriptionUsage.tier !== 'premium' && (
                <button
                  type="button"
                  onClick={() => setIsUpgradeModalOpen(true)}
                  className="px-3 py-1 bg-gradient-to-r from-red-600 to-orange-500 text-white font-bold rounded-lg hover:opacity-90 transition text-[11px] shadow-sm"
                >
                  Upgrade to Unlimited &rarr;
                </button>
              )}
            </div>
          )}

          <div className="bg-[#090d16] border border-white/[0.08] rounded-2xl p-4 flex flex-col sm:flex-row justify-between sm:items-center gap-2">
            <div>
              <div className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">
                {isSelfOnboarding ? 'Generated Digital Tag ID' : 'Physical Tag Identifier'}
              </div>
              <div className="font-mono text-lg font-black text-orange-400">{tagId}</div>
            </div>
            <div className="text-left sm:text-right">
              <div className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">
                {isSelfOnboarding ? 'Onboarding Mode' : 'Assigned Outlet'}
              </div>
              <div className="text-xs font-bold text-white truncate max-w-xs">
                {isSelfOnboarding ? 'Direct Self-Registration (No Outlet Sticker Needed)' : blankQr?.outletName || 'Retail Distribution Hub'}
              </div>
            </div>
          </div>
        </div>

        {/* Step Progress Timeline */}
        <div className="glass-pill-container p-2 rounded-2xl flex items-center justify-between text-[11px] font-bold">
          <div className="flex items-center gap-1.5 text-emerald-400 px-3">
            <span className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center text-xs">1</span>
            <span>Sticker Affixed</span>
          </div>
          <div className="h-px bg-white/10 flex-1 mx-2"></div>
          <div className="flex items-center gap-1.5 text-orange-400 px-3">
            <span className="w-5 h-5 rounded-full bg-orange-500/20 flex items-center justify-center text-xs">2</span>
            <span>Hardware Specs</span>
          </div>
          <div className="h-px bg-white/10 flex-1 mx-2"></div>
          <div className="flex items-center gap-1.5 text-slate-500 px-3">
            <span className="w-5 h-5 rounded-full bg-white/5 flex items-center justify-center text-xs">3</span>
            <span>Verification</span>
          </div>
        </div>

        {/* Registration Form */}
        <form onSubmit={handleRegisterDevice} className="glass-card rounded-3xl p-6 md:p-8 space-y-6 border border-white/[0.08]">
          {/* Step 1: Confirmation */}
          <div className="bg-orange-500/10 border border-orange-500/30 rounded-2xl p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={attachedConfirmed}
                onChange={(e) => setAttachedConfirmed(e.target.checked)}
                className="mt-1 h-4 w-4 text-orange-500 rounded border-slate-700 bg-slate-950 focus:ring-orange-500"
              />
              <div className="text-xs text-slate-300">
                <span className="font-bold text-white block mb-0.5">
                  Step 1: Physical Sticker Confirmation
                </span>
                I confirm that I have picked up this physical Verisure QR sticker tag and attached it firmly to my device.
              </div>
            </label>
          </div>

          <div>
            <h2 className="text-lg font-bold text-white mb-1">Step 2: Device Specifications</h2>
            <p className="text-xs text-slate-400">
              Enter device details to bind them immutably to this QR code.
            </p>
          </div>

          {/* Manual Tag ID input if accessed directly via /tag */}
          {!urlTagId && (
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">
                Enter Physical Sticker Tag ID
              </label>
              <input
                type="text"
                value={tagId}
                onChange={(e) => setTagId(e.target.value.trim().toUpperCase())}
                placeholder="e.g. VRS-TAG-10492"
                className="w-full bg-slate-950 border border-slate-700 text-orange-400 font-mono font-bold rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-500"
                required
              />
            </div>
          )}

          {/* Category */}
          <div>
            <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
              Device Category
            </label>
            <select
              value={category}
              onChange={(e: any) => {
                setCategory(e.target.value);
                const brands = POPULAR_BRANDS[e.target.value] || POPULAR_BRANDS.consumer_electronics;
                setMake(brands[0]);
              }}
              className="w-full bg-slate-950 border border-slate-700 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-500"
            >
              <option value="consumer_electronics">Consumer Electronics (Phone, Laptop, Tablet, Audio)</option>
              <option value="home_appliance">Home Appliance (Refrigerator, AC, TV, Washing Machine)</option>
              <option value="industrial">Industrial Equipment (Generators, Turbines, Pumps)</option>
              <option value="electrical">Electrical & Power (Inverters, Batteries, Breakers)</option>
              <option value="telecom">Telecom & Fiber (Splicers, OTDR, Switches)</option>
              <option value="vehicle">Automobile & EV (Vehicles, Bikes, Fleet)</option>
              <option value="other">Other High-Value Equipment</option>
            </select>
          </div>

          {/* Make / Brand Selection */}
          <div>
            <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
              Brand / Manufacturer
            </label>
            {/* Quick Brand Badges */}
            <div className="flex flex-wrap gap-2 mb-2">
              {(POPULAR_BRANDS[category] || POPULAR_BRANDS.consumer_electronics).map((b) => (
                <button
                  key={b}
                  type="button"
                  onClick={() => handleBrandSelect(b)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition border ${
                    make === b
                      ? 'bg-orange-500 border-orange-400 text-white'
                      : 'bg-slate-950 border-slate-800 text-slate-400 hover:text-white'
                  }`}
                >
                  {b}
                </button>
              ))}
            </div>

            {make === 'Other' && (
              <input
                type="text"
                value={customMake}
                onChange={(e) => setCustomMake(e.target.value)}
                placeholder="Enter Brand / Manufacturer Name"
                className="w-full bg-slate-950 border border-slate-700 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-500 mt-2"
                required
              />
            )}
          </div>

          {/* Model Name */}
          <div>
            <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">
              Device Model
            </label>
            <input
              type="text"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder="e.g. iPhone 16 Pro (256GB Desert Titanium) or ThinkPad X1 Carbon"
              className="w-full bg-slate-950 border border-slate-700 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-500"
              required
            />
          </div>

          {/* Serial Number / IMEI */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider">
                Serial Number / IMEI (Anti-Theft Identifier)
              </label>
              <span className="text-[10px] text-orange-400 font-semibold">Recommended for Warranty</span>
            </div>
            <input
              type="text"
              value={serialNumber}
              onChange={(e) => setSerialNumber(e.target.value)}
              placeholder="e.g. 356982104928174 or C02XL198JHD2"
              className="w-full bg-slate-950 border border-slate-700 text-white rounded-xl px-4 py-3 text-sm font-mono focus:outline-none focus:border-orange-500"
            />
            <span className="text-[11px] text-slate-500 mt-1 block">
              Entering the Serial/IMEI ensures replacement parts during technician repairs are checked against this authentic ID.
            </span>
          </div>

          {/* Year & Value */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">
                Manufacture Year
              </label>
              <input
                type="number"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                min={2000}
                max={2030}
                className="w-full bg-slate-950 border border-slate-700 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-500 font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">
                Purchase Price / MSRP
              </label>
              <input
                type="number"
                value={estimatedPrice}
                onChange={(e) => setEstimatedPrice(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="e.g. 1500000"
                className="w-full bg-slate-950 border border-slate-700 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-500 font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">
                Currency
              </label>
              <select
                value={priceCurrency}
                onChange={(e) => setPriceCurrency(e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-500"
              >
                <option value="NGN">NGN (₦)</option>
                <option value="USD">USD ($)</option>
                <option value="GBP">GBP (£)</option>
                <option value="EUR">EUR (€)</option>
              </select>
            </div>
          </div>

          {/* Initial Condition Score */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider">
                Initial Condition Score: <span className="text-emerald-400 font-mono font-black">{conditionScore}/100</span>
              </label>
              <div className="flex gap-1">
                {[100, 95, 90, 85].map((score) => (
                  <button
                    key={score}
                    type="button"
                    onClick={() => setConditionScore(score)}
                    className={`px-2 py-0.5 rounded text-[10px] font-bold transition border ${
                      conditionScore === score
                        ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40'
                        : 'bg-slate-950 text-slate-400 border-slate-800 hover:text-white'
                    }`}
                  >
                    {score === 100 ? '100% (Brand New)' : `${score}%`}
                  </button>
                ))}
              </div>
            </div>
            <input
              type="range"
              min="50"
              max="100"
              value={conditionScore}
              onChange={(e) => setConditionScore(Number(e.target.value))}
              className="w-full accent-emerald-500 h-1.5 bg-slate-950 rounded-lg cursor-pointer"
            />
          </div>

          {/* Owner Details */}
          <div className="border-t border-slate-800 pt-5 space-y-4">
            <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider">
              Step 3: Owner Contact Information
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Full Name / Organization</label>
                <input
                  type="text"
                  value={ownerName}
                  onChange={(e) => setOwnerName(e.target.value)}
                  placeholder="e.g. Emeka Adeleke"
                  className="w-full bg-slate-950 border border-slate-700 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-500"
                  required
                />
              </div>

              <div>
                <label className="block text-xs text-slate-400 mb-1">Phone Number (For Service SMS)</label>
                <input
                  type="tel"
                  value={ownerPhone}
                  onChange={(e) => setOwnerPhone(e.target.value)}
                  placeholder="e.g. +234 803 123 4567"
                  className="w-full bg-slate-950 border border-slate-700 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs text-slate-400 mb-1">Email Address</label>
              <input
                type="email"
                value={ownerEmail}
                onChange={(e) => setOwnerEmail(e.target.value)}
                placeholder="e.g. owner@example.com"
                className="w-full bg-slate-950 border border-slate-700 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-500"
              />
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 hover:opacity-95 text-white font-black py-4 rounded-2xl shadow-xl shadow-orange-500/20 transition flex items-center justify-center gap-2 text-base disabled:opacity-50"
          >
            {submitting ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                <span>Activating Verisure QR Identity...</span>
              </>
            ) : (
              <span>Complete Registration & Activate Verisure Identity</span>
            )}
          </button>
        </form>
      </div>

      <UpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        usage={subscriptionUsage}
        onUpgradeSuccess={() => loadUsage()}
      />
    </div>
  );
};
